import { Box, Avatar, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { CustomDialogComponent } from "../../../shared/elements/customDialog.component";
import { CustomInputComponent } from "../../../shared/elements/customInputs.component";
import { useGetMyProfileHook, useUpdateMyProfileHook } from "../hooks/profile.hook";

const emptyFormData = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
};

export const ProfileComponent: React.FC = () => {
  const { data: profile, isLoading, error } = useGetMyProfileHook();
  const updateProfileMutation = useUpdateMyProfileHook();

  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);
  const [formError, setFormError] = useState<string | null>(null);

  const handleOpenEdit = () => {
    if (!profile) return;
    setFormData({
      firstName: profile.firstName,
      lastName: profile.lastName,
      email: profile.email,
      password: "",
    });
    setFormError(null);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setFormData(emptyFormData);
    setFormError(null);
  };

  const handleInputChange =
    (field: keyof typeof formData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormData({ ...formData, [field]: event.target.value });
    };

  const handleConfirm = () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setFormError("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    if (formData.password && formData.password.length < 8) {
      setFormError("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    updateProfileMutation.mutate(
      {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        ...(formData.password ? { password: formData.password } : {}),
      },
      {
        onSuccess: () => handleClose(),
        onError: (err: any) =>
          setFormError(
            err?.response?.data?.message || "Une erreur est survenue."
          ),
      }
    );
  };

  if (isLoading) {
    return (
      <Box p={2} textAlign="center" color="#999" fontSize="0.85rem">
        Chargement...
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box p={2} textAlign="center" color="#c0392b" fontSize="0.85rem">
        Erreur lors du chargement du profil.
      </Box>
    );
  }

  return (
    <Box p={2} display="flex" justifyContent="center">
      <Box
        sx={{
          width: { xs: "100%", sm: "360px" },
          backgroundColor: "#FFFDF7",
          borderRadius: "16px",
          padding: "28px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <Box position="relative">
          <Avatar
            src={profile.avatar || ""}
            alt={profile.fullName}
            sx={{ width: 100, height: 100, border: "3px solid #B4006E" }}
          />
          <IconButton
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              backgroundColor: "#B4006E",
              color: "white",
              width: 30,
              height: 30,
              "&:hover": { backgroundColor: "#900050" },
            }}
          >
            <CameraAltIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Typography fontSize="1.1rem" fontWeight="bold" color="#B4006E">
          {profile.fullName}
        </Typography>

        <Box width="100%" display="flex" flexDirection="column" gap={1.5} mt={1}>
          <Box>
            <Box fontSize="12px" fontWeight="bold" color="#333" mb={0.5}>
              Adresse email :
            </Box>
            <Box fontSize="13px" color="#555">
              {profile.email}
            </Box>
          </Box>
          <Box>
            <Box fontSize="12px" fontWeight="bold" color="#333" mb={0.5}>
              Rôle :
            </Box>
            <Box fontSize="13px" color="#555">
              {profile.role}
            </Box>
          </Box>
        </Box>

        <Typography
          onClick={handleOpenEdit}
          sx={{
            color: "#B4006E",
            fontWeight: "bold",
            fontSize: "13px",
            cursor: "pointer",
            marginTop: "12px",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Modifier mon profil
        </Typography>
      </Box>

      <CustomDialogComponent
        open={openDialog}
        onClose={handleClose}
        title="Modifier mon profil"
        onConfirm={handleConfirm}
        confirmLabel="Enregistrer"
        cancelLabel="Annuler"
      >
        <Box display="flex" flexDirection="column" gap={3} py={2}>
          <CustomInputComponent
            label="Prénom"
            value={formData.firstName}
            onChange={handleInputChange("firstName")}
            placeholder="Entrez le prénom"
          />
          <CustomInputComponent
            label="Nom"
            value={formData.lastName}
            onChange={handleInputChange("lastName")}
            placeholder="Entrez le nom"
          />
          <CustomInputComponent
            label="Adresse email"
            value={formData.email}
            onChange={handleInputChange("email")}
            type="email"
            placeholder="Entrez l'adresse email"
          />
          <CustomInputComponent
            label="Nouveau mot de passe (optionnel)"
            value={formData.password}
            onChange={handleInputChange("password")}
            type="password"
            placeholder="Laisser vide pour ne pas changer"
          />
          {formError && (
            <Box color="#c0392b" fontSize="0.8rem">
              {formError}
            </Box>
          )}
          {updateProfileMutation.isPending && (
            <Box color="#999" fontSize="0.8rem">
              Enregistrement...
            </Box>
          )}
        </Box>
      </CustomDialogComponent>
    </Box>
  );
};
